import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { AnalystMessageView } from '@/components/profile/AnalystMessageView';
import { ChatMarkdownRenderer } from '@/components/chat/ChatMarkdownRenderer';
import type { AnalystMessage } from '@shared/types/company-analyst';

interface Props {
  messages: AnalystMessage[];
  running: boolean;
  live: string;
  error: string | null;
  onSend: (text: string | null) => void;
  onStop: () => void;
}

/** The objectives conversation: the analyst explains its table; the manager challenges, changes, adds. */
export const GoalsChat = ({ messages, running, live, error, onSend, onStop }: Props) => {
  const [draft, setDraft] = useState('');
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [messages, live]);
  const first = !messages.some((message) => message.role === 'analyst' && !message.failed);
  const send = () => { if (draft.trim() && !running) { onSend(draft.trim()); setDraft(''); } };

  return (
    <aside className="flex h-full min-h-[60vh] flex-col overflow-hidden rounded-xl border border-domino-cardBorder bg-domino-warm shadow-[var(--domino-shadow-soft)]">
      <header className="border-b border-domino-cardBorder px-4 py-3">
        <p className="font-semibold text-domino-ink">Objectifs</p>
      </header>
      <div className="flex-1 space-y-4 overflow-y-auto bg-domino-ivory/60 px-4 py-4">
        {messages.length === 0 && !running && (
          <Button onClick={() => onSend(null)}>Proposer à partir de la qualification</Button>
        )}
        {messages.map((message) => <AnalystMessageView key={message.id} message={message} />)}
        {running && (
          <div className="rounded-2xl rounded-tl-md border border-domino-cardBorder bg-white/70 px-4 py-3">
            {live
              ? <ChatMarkdownRenderer content={live} className="text-sm leading-6 text-domino-ink [&_strong]:text-domino-aubergine" />
              : (
                <p className="text-sm leading-6 text-domino-inkSoft">
                  {first
                    ? 'L’analyste relit la qualification et prépare une première version de vos objectifs, avec leur cible, leur échéance et leur criticité. Comptez une minute environ.'
                    : 'L’analyste reprend votre demande et met le tableau à jour. Comptez une minute environ.'}
                </p>
              )}
          </div>
        )}
        <div ref={bottom} />
      </div>
      <footer className="space-y-2 border-t border-domino-cardBorder p-3">
        {error && <p className="text-sm text-red-700">{error}</p>}
        <form className="flex gap-2" onSubmit={(event) => { event.preventDefault(); send(); }}>
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(); }
            }}
            rows={2}
            placeholder="Challengez, modifiez ou ajoutez un objectif… (Entrée pour envoyer)"
            className="text-sm"
            disabled={running}
          />
          {running
            ? <Button type="button" variant="secondary" onClick={onStop}>Arrêter</Button>
            : <Button type="submit" disabled={!draft.trim()}>Envoyer</Button>}
        </form>
      </footer>
    </aside>
  );
};
